/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  formatFecha,
  urlBaseAPI,
  Titulo,
  Sidebar,
  CampoInformacion,
  TablaFiltrable
} from "../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Image } from "primereact/image";

// Card de muestras del proceso de la actividad abierta. Las relacionadas a la
// actividad actual vienen primero (la API ya las ordena); al seleccionar una
// se abre un sidebar con los detalles y las fotos.
export default function MuestrasProceso({ pasoId }) {
  const [loader, setLoader] = useState(true);
  const [muestras, setMuestras] = useState([]);
  const [muestraSeleccionada, setMuestraSeleccionada] = useState(null);
  const [sideBar, setSideBar] = useState(false);

  const toast = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      `colab/actividad/${pasoId}/muestras`,
      null,
      (respuesta) => {
        setMuestras(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar muestras",
          sticky: true,
        });
        setLoader(false);
      }
    );
  }, [pasoId]);

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {/* Sidebar con los detalles de la muestra */}
      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {muestraSeleccionada && (
          <div css={{ marginBottom: "50px" }}>
            <Titulo>Muestra {muestraSeleccionada.numero}</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Producto">
                {muestraSeleccionada.productoEtiqueta || "—"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Actividad">
                {muestraSeleccionada.actividadNombre || "—"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Caja">
                {muestraSeleccionada.cajaNumero !== null
                  ? `${muestraSeleccionada.cajaNumero}${
                      muestraSeleccionada.cajaDescripcion
                        ? ` - ${muestraSeleccionada.cajaDescripcion}`
                        : ""
                    }`
                  : "—"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de adquisición">
                {muestraSeleccionada.fechaadquisicion
                  ? formatFecha(muestraSeleccionada.fechaadquisicion)
                  : "—"}
              </CampoInformacion>
              {muestraSeleccionada.notas && (
                <CampoInformacion nombreCampo="Notas" anchoCompleto>
                  {muestraSeleccionada.notas}
                </CampoInformacion>
              )}
            </div>
            {muestraSeleccionada.imagenes?.length > 0 && (
              <>
                <Titulo>Fotos</Titulo>
                <div
                  css={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5em",
                  }}
                >
                  {muestraSeleccionada.imagenes.map((imagen) => (
                    <Image
                      key={imagen.id}
                      src={`${urlBaseAPI()}/archivo/${imagen.hash}/preview`}
                      alt={imagen.nombredescarga}
                      preview
                      imageStyle={{
                        width: "140px",
                        height: "140px",
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Sidebar>

      <Card>
        <Titulo>
          <i className="pi pi-inbox" css={{ marginRight: "0.5em" }}></i>
          Muestras
        </Titulo>
        {loader && (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} width="100%" height="2.5rem" />
            ))}
          </div>
        )}
        {!loader && (
          <TablaFiltrable conFiltros={false}
            value={muestras}
            emptyMessage="No hay muestras para este proceso"
            size="small"
            onRowClick={(e) => {
              setMuestraSeleccionada(e.data);
              setSideBar(true);
            }}
            css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
          >
            <Column
              header="Producto"
              body={(rowData) => (
                <span
                  css={{ display: "flex", alignItems: "center", gap: "0.5em" }}
                >
                  {rowData.productoEtiqueta || `Muestra ${rowData.numero}`}
                  {rowData.deActividad && (
                    <Tag
                      value="Actividad"
                      severity="info"
                      css={{ fontSize: "0.7em" }}
                    />
                  )}
                </span>
              )}
            />
            <Column
              header="Fecha de adquisición"
              body={(rowData) =>
                rowData.fechaadquisicion
                  ? formatFecha(rowData.fechaadquisicion)
                  : "—"
              }
              style={{ width: "11em" }}
            />
          </TablaFiltrable>
        )}
      </Card>
    </>
  );
}
